import pdpDealsBannerDataEN from '../../../../prismic-pdp-deals-banner-data-EN.json';
import pdpDealsBannerDataFR from '../../../../prismic-pdp-deals-banner-data-FR.json';
import type { PdpDealsBannerDocument } from '../../../../prismicio-types';
import {
  PdpDealsBannerEntry,
  PdpDealsBannerMatchRule,
} from '../../types/pdpDealsBanner';

type PrismicMatchRuleField = {
  brand?: string | null;
  category?: string | null;
  sticker_type?: string | null;
  style?: string | null;
  variant_sku?: string | null;
  product_code?: string | null;
  price_group?: string | null;
};

// RichTextField items are RTNode which TypeScript types strictly — at runtime
// Prismic paragraph nodes always have a `text` property, so we cast safely.
// All blocks are joined so multi-paragraph content authored in Prismic renders fully.
const getMarkdownText = (field?: unknown) =>
  (field as { text?: string }[] | undefined)
    ?.map((block) => block.text ?? '')
    .filter(Boolean)
    .join('\n\n') ?? '';

const mapMatchRule = (
  item: PrismicMatchRuleField
): PdpDealsBannerMatchRule => ({
  brand: item.brand ?? undefined,
  category: item.category ?? undefined,
  stickerType: item.sticker_type ?? undefined,
  style: item.style ?? undefined,
  variantSku: item.variant_sku ?? undefined,
  productCode: item.product_code ?? undefined,
  priceGroup: item.price_group ?? undefined,
});

const getMatchRules = (
  field?: PrismicMatchRuleField[]
): PdpDealsBannerMatchRule[] =>
  (field ?? [])
    .map(mapMatchRule)
    .filter((rule) => Object.values(rule).some(Boolean));

const mapDocumentToBannerEntry = (
  document: PdpDealsBannerDocument,
  index: number
): PdpDealsBannerEntry => ({
  id: document.uid ?? document.id ?? `pdp-deals-banner-${index}`,
  prismicId: document.id ?? undefined,
  enabled: document.data?.enabled,
  priority: document.data?.priority ?? undefined,
  position: document.data?.position
    ? (Number(document.data.position) as 1 | 2)
    : 1,
  appearance: document.data?.appearance ?? 'yellow',
  title: document.data?.title ?? '',
  firstLineMarkdown: getMarkdownText(document.data?.first_line_markdown),
  descriptionMarkdown: getMarkdownText(document.data?.description_markdown),
  tooltipMarkdown: getMarkdownText(document.data?.tooltip_markdown),
  tooltipPlacement: document.data?.tooltip_placement,
  ctaLabel: document.data?.cta_label ?? '',
  ctaHref: document.data?.cta_href ?? '',
  startDate: document.data?.start_date ?? undefined,
  endDate: document.data?.end_date ?? undefined,
  targetAllProducts: document.data?.target_all_products,
  includeRules: getMatchRules(document.data?.include_rules),
  excludeRules: getMatchRules(document.data?.exclude_rules),
});

const getPdpDealsBannerData = (locale: string): PdpDealsBannerEntry[] => {
  const documents = locale.startsWith('fr')
    ? (pdpDealsBannerDataFR as unknown as PdpDealsBannerDocument[])
    : (pdpDealsBannerDataEN as unknown as PdpDealsBannerDocument[]);

  return documents.map(mapDocumentToBannerEntry);
};

export default getPdpDealsBannerData;
